/**
 * Габариты экземпляра объекта каталога и их смена.
 *
 * Итоговый размер — личное значение поверх каталожного. Слой хранит только то, что
 * пользователь поменял: вернул каталожный размер — личное значение стирается.
 *
 * Смена размера держит на месте одну сторону предмета, а не центр. Иначе шкаф,
 * которому уменьшили глубину, отъезжает задней стенкой от стены, а кровать, которую
 * тянут за правый край, расползается в обе стороны. Какая сторона стоит, решает тот,
 * кто меняет: ручка на предмете — противоположная своей стороне, поле в панели — по
 * смыслу оси (`defaultResizeAnchor`): ширина растёт в обе стороны, глубина — вперёд
 * от спинки, высота — вверх от низа.
 *
 * Растущий предмет упирается в стены (`fitSizeToWalls`): кровать, которую тянут к стене,
 * останавливается вплотную к ней, а не уходит внутрь. Вплотную можно — касание не
 * пересечение. Предмет, который уже стоит в стене, не держим, иначе его не растянуть
 * и не сузить обратно.
 */
import {
	SIZE_STEP,
	clampItemSize,
	type CatalogItem,
	type SizeAxis,
	type View3D,
} from "@/shared/catalog";
import type { Pt } from "@/shared/lib/geometry";
import type { Fixture, FloorItem, WallGraph } from "./types-def";

type DimensionOverrides = Pick<Fixture | FloorItem, "width" | "height" | "depth">;

export const itemDimensions = (
	item: DimensionOverrides,
	catalogItem: Pick<CatalogItem, "width" | "height" | "depth">,
) => ({
	width: item.width ?? catalogItem.width,
	height: item.height ?? catalogItem.height,
	depth: item.depth ?? catalogItem.depth,
});

/**
 * Какая сторона предмета стоит на месте при смене размера, в осях символа:
 * `start` — меньшая координата (левый край по ширине, верхний по глубине),
 * `end` — противоположная, `center` — растёт в обе стороны.
 */
export type ResizeAnchor = "start" | "center" | "end";

/**
 * Куда смотрит перед символа по его оси Y: 1 — к нижнему краю, −1 — к верхнему,
 * 0 — вбок. Модели смотрят в +Z, то есть к нижнему краю символа; модель файлом,
 * развёрнутую `turn` на 180°, развернули потому, что перед символа у неё сверху (диван).
 */
const frontAlongDepth = (view: View3D): 1 | -1 | 0 => {
	if (view.type !== "model") return 1;
	const turn = (((view.turn ?? 0) % 360) + 360) % 360;
	if (turn === 0) return 1;
	if (turn === 180) return -1;
	return 0;
};

/** Сторона, которая стоит при смене размера из панели: глубина растёт вперёд от спинки. */
export const defaultResizeAnchor = (catalogItem: CatalogItem, axis: SizeAxis): ResizeAnchor => {
	if (axis !== "depth") return "center";
	const front = frontAlongDepth(catalogItem.view3d);
	// Спинка — напротив переда: перед снизу — спинка сверху (start), и наоборот
	if (front === 1) return "start";
	if (front === -1) return "end";
	return "center";
};

/** Точка в осях предмета (ширина — x, глубина — y) -> точка плана: поворот как у символа. */
const toPlan = (item: FloorItem, local: Pt): Pt => {
	const angle = (item.rotation * Math.PI) / 180;
	const cos = Math.cos(angle);
	const sin = Math.sin(angle);
	return { x: item.x + local.x * cos - local.y * sin, y: item.y + local.x * sin + local.y * cos };
};

/** Центр предмета, у которого по оси стал размер `size`, а сторона `anchor` осталась на месте. */
const centerForSize = (
	item: FloorItem,
	catalogItem: CatalogItem,
	axis: "width" | "depth",
	size: number,
	anchor: ResizeAnchor,
): Pt => {
	if (anchor === "center") return { x: item.x, y: item.y };
	const grown = size - itemDimensions(item, catalogItem)[axis];
	// Стоит начало — центр уходит к концу на половину прироста, и наоборот
	const shift = ((anchor === "start" ? 1 : -1) * grown) / 2;
	return toPlan(item, axis === "width" ? { x: shift, y: 0 } : { x: 0, y: shift });
};

/** На сколько миллиметров предмет может зайти на стену, чтобы это считалось касанием. */
const TOUCH = 1;

/** Контур предмета на плане при размере `size` по оси: четыре угла прямоугольника. */
const footprintAt = (
	item: FloorItem,
	catalogItem: CatalogItem,
	axis: "width" | "depth",
	size: number,
	anchor: ResizeAnchor,
): Pt[] => {
	const center = centerForSize(item, catalogItem, axis, size, anchor);
	const dims = { ...itemDimensions(item, catalogItem), [axis]: size };
	const moved = { ...item, ...center };
	const w = dims.width / 2;
	const d = dims.depth / 2;
	return [
		{ x: -w, y: -d },
		{ x: w, y: -d },
		{ x: w, y: d },
		{ x: -w, y: d },
	].map((corner) => toPlan(moved, corner));
};

/** Полоса стены на плане: от узла до узла на всю толщину. Стыки углов не важны — это упор. */
const wallStrip = (graph: WallGraph, a: string, b: string, thickness: number): Pt[] | null => {
	const p = graph.nodes[a];
	const q = graph.nodes[b];
	if (!p || !q) return null;
	const length = Math.hypot(q.x - p.x, q.y - p.y);
	if (length === 0) return null;
	const nx = (-(q.y - p.y) / length) * (thickness / 2);
	const ny = ((q.x - p.x) / length) * (thickness / 2);
	return [
		{ x: p.x + nx, y: p.y + ny },
		{ x: q.x + nx, y: q.y + ny },
		{ x: q.x - nx, y: q.y - ny },
		{ x: p.x - nx, y: p.y - ny },
	];
};

/** Перекрываются ли два выпуклых многоугольника глубже касания (теорема о разделяющей оси). */
const overlaps = (first: Pt[], second: Pt[]): boolean => {
	for (const poly of [first, second]) {
		for (let i = 0; i < poly.length; i++) {
			const a = poly[i];
			const b = poly[(i + 1) % poly.length];
			const axis = { x: a.y - b.y, y: b.x - a.x };
			const length = Math.hypot(axis.x, axis.y);
			if (length === 0) continue;
			const project = (points: Pt[]) =>
				points.map((point) => (point.x * axis.x + point.y * axis.y) / length);
			const p = project(first);
			const q = project(second);
			if (Math.max(...p) <= Math.min(...q) + TOUCH || Math.max(...q) <= Math.min(...p) + TOUCH)
				return false;
		}
	}
	return true;
};

const hitsWall = (graph: WallGraph, footprint: Pt[]): boolean =>
	Object.values(graph.walls).some((wall) => {
		const strip = wallStrip(graph, wall.a, wall.b, wall.thickness);
		return strip !== null && overlaps(footprint, strip);
	});

/**
 * Наибольший размер по оси не больше `size`, при котором растущий предмет не заходит
 * в стены. Уменьшать можно всегда; предмет, уже стоящий в стене, не ограничиваем.
 */
export const fitSizeToWalls = (
	item: FloorItem,
	catalogItem: CatalogItem,
	axis: "width" | "depth",
	size: number,
	anchor: ResizeAnchor,
	graph: WallGraph,
): number => {
	const current = itemDimensions(item, catalogItem)[axis];
	if (size <= current) return size;
	const hits = (value: number) =>
		hitsWall(graph, footprintAt(item, catalogItem, axis, value, anchor));
	if (hits(current) || !hits(size)) return size;
	// Делим пополам до миллиметра, потом отступаем к шагу размеров
	let fits = current;
	let blocked = size;
	while (blocked - fits > 1) {
		const middle = (fits + blocked) / 2;
		if (hits(middle)) blocked = middle;
		else fits = middle;
	}
	return Math.max(current, Math.floor(fits / SIZE_STEP) * SIZE_STEP);
};

/**
 * Правка предмета на полу для нового размера по оси: размер в пределах каталога,
 * и центр, сдвинутый так, чтобы сторона `anchor` осталась на месте. Высота растёт
 * вверх от низа — центр на плане не двигается. С графом стен (`walls`) растущий
 * предмет останавливается у стены (`fitSizeToWalls`).
 */
export const resizeFloorItem = (
	item: FloorItem,
	catalogItem: CatalogItem,
	axis: SizeAxis,
	value: number,
	anchor: ResizeAnchor = defaultResizeAnchor(catalogItem, axis),
	walls?: WallGraph,
): Partial<Omit<FloorItem, "id">> => {
	const clamped = clampItemSize(catalogItem, axis, value);
	const size =
		walls && axis !== "height"
			? fitSizeToWalls(item, catalogItem, axis, clamped, anchor, walls)
			: clamped;
	const patch: Partial<Omit<FloorItem, "id">> = {
		[axis]: size === catalogItem[axis] ? undefined : size,
	};
	if (axis === "height" || anchor === "center") return patch;

	const center = centerForSize(item, catalogItem, axis, size, anchor);
	return { ...patch, x: Math.round(center.x), y: Math.round(center.y) };
};

/**
 * Сырой размер по оси, до которого дотянули сторону предмета: от стоящей стороны
 * (`anchor`) до точки плана, в осях предмета. Пределы и шаг навешивает `resizeFloorItem`.
 */
export const sizeTowardsPoint = (
	origin: FloorItem,
	catalogItem: CatalogItem,
	axis: "width" | "depth",
	anchor: ResizeAnchor,
	point: { x: number; y: number },
): number => {
	// Точка — в оси предмета: поворачиваем её обратно вокруг его центра
	const angle = (origin.rotation * Math.PI) / 180;
	const dx = point.x - origin.x;
	const dy = point.y - origin.y;
	const local =
		axis === "width"
			? dx * Math.cos(angle) + dy * Math.sin(angle)
			: -dx * Math.sin(angle) + dy * Math.cos(angle);
	const half = itemDimensions(origin, catalogItem)[axis] / 2;
	// Стоит начало — размер от него до точки, стоит конец — от точки до него
	return anchor === "start" ? local + half : half - local;
};
